import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  ShipmentGeofencePolygonViewModel,
  ShipmentGeofenceViewModel,
  ShipmentLocationViewModel,
} from '../shipment-location-list-table/shipment-location-list-table.component';
import { MatRadioModule } from '@angular/material/radio';
import {
  DEFAULT_RADIUS_METERS,
  ShipmentGeofencePolygonDto,
  ShipmentPlace,
} from '@entities/shipment-location';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { firstValueFrom } from 'rxjs';
import { ShipmentLocationMapComponent } from '../shipment-location-map/shipment-location-map.component';
import { ShipmentLocationDeviceViewModel } from '../shipment-location-map/shipment-location-map.component';
import { ShipmentLocationService } from '@services/api/shipment-location.service';
import {
  MatSlideToggleChange,
  MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import * as turf from '@turf/turf';

/** 位置情報のフォームグループ */
export type ShipmentLocationFormGroup = FormGroup<{
  /** 位置情報住所 */
  address: FormControl<string>;
  /** 郵便番号 */
  postalCode: FormControl<string | null>;
  /** 緯度 */
  latitude: FormControl<number | null>;
  /** 経度 */
  longitude: FormControl<number | null>;
  /** 位置情報名 */
  locationName: FormControl<string>;
  /** ジオフェンスタイプ */
  geofenceType: FormControl<GeofenceType>;
  /** 円の半径 */
  circleRadius: FormControl<number>;
  /** 単位 */
  unit: FormControl<UnitType>;
  /** ポリゴンリスト */
  polygonList: FormControl<ShipmentGeofencePolygonDto[]>;
  /** 頂点の数 */
  numberOfVertices: FormControl<number>;
}>;

/** ジオフェンス種類 */
export type GeofenceType = 'circle' | 'polygon';

/** 単位 */
export type UnitType = 'm' | 'km' | 'mile';

/** 位置情報のViewModel */
export type ShipmentLocationEditViewModel = {
  /** 更新するか */
  updating: boolean;
  /** 位置情報住所 */
  address: string;
  /** 位置情報名 */
  locationName: string;
  /** ジオフェンスタイプ */
  geofenceType: GeofenceType;
  /** 円の半径（元の指定した値） */
  circleRadius: number;
  /** 円の半径の単位 */
  circleRadiusUnit: UnitType;
  /** 円の半径（メートル単位） */
  circleRadiusInMeters: number;
  /** 頂点の数 */
  numberOfVertices: number;
  /** ポリゴンリスト */
  polygonList: ShipmentGeofencePolygonDto[];
  /** 緯度 */
  latitude: number;
  /** 経度 */
  longitude: number;
  /** 郵便番号 */
  postalCode: string;
  /** 配送もしくはルート設定で使用されている位置情報か */
  isUse: boolean;
  /** 配送中のルート設定で使用されている位置情報か */
  isShipping: boolean;
  /** 登録済み位置情報 */
  registeredLocationList?: ShipmentLocationViewModel[];
  /** 地図表示用デバイス位置情報 */
  deviceLocationList?: ShipmentLocationDeviceViewModel[];
};

/** 位置情報の場所候補 */
type ShipmentPlaceSuggestion = {
  /** テキスト */
  text: string;
  /** 場所ID */
  placeId: string;
};

@Component({
  selector: 'app-shipment-location-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatRadioModule,
    MatIconModule,
    MatButtonModule,
    MatAutocompleteModule,
    ShipmentLocationMapComponent,
    MatSlideToggleModule,
  ],
  templateUrl: './shipment-location-edit-dialog.component.html',
  styleUrl: './shipment-location-edit-dialog.component.scss',
})
export class ShipmentLocationEditDialogComponent implements OnInit {
  @ViewChild(MatAutocompleteTrigger)
  autocompleteTrigger!: MatAutocompleteTrigger;

  /** 更新か */
  public updating: boolean;

  /** 位置情報 */
  public shipmentLocation: ShipmentLocationViewModel | undefined;

  /** 場所候補リスト */
  public placeSuggestionList: ShipmentPlaceSuggestion[] = [];

  /** フォームグループ */
  public formGroup: ShipmentLocationFormGroup;

  /** 登録済み位置情報リスト */
  private registeredLocationList: ShipmentLocationViewModel[];

  /** マップ表示用の登録済み位置情報リスト */
  public displayRegisteredLocationList: ShipmentLocationViewModel[];
  public isRegisteredLocationVisible: boolean;
  /** マップ表示用のデバイス位置情報リスト */
  public readonly deviceLocationList: ShipmentLocationDeviceViewModel[];

  /** 配送またはルート設定で使用されているか */
  public readonly isUse: boolean;

  /** 配送中のルート設定で使用されているか */
  public readonly isShipping: boolean;

  /** マップをクリックして位置情報を検索した時の候補リスト */
  private placeDetailList: ShipmentPlace[];

  constructor(
    @Inject(MAT_DIALOG_DATA) options: ShipmentLocationEditViewModel,
    private dialogRef: MatDialogRef<ShipmentLocationEditDialogComponent>,
    private dialog: MatDialog,
    private shipmentLocationService: ShipmentLocationService,
  ) {
    this.registeredLocationList = options.registeredLocationList || [];
    this.isRegisteredLocationVisible = true;
    this.displayRegisteredLocationList = this.registeredLocationList;
    this.deviceLocationList = options.deviceLocationList || [];
    this.placeDetailList = [];
    this.isUse = options.isUse;
    this.isShipping = options.isShipping;

    this.updating = options.updating;

    this.formGroup = new FormGroup({
      address: new FormControl(options.address || '', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(100)],
      }),
      postalCode: new FormControl(options.postalCode || null),
      latitude: new FormControl(options.latitude || null, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      longitude: new FormControl(options.longitude || null, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      locationName: new FormControl(options.locationName || '', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(100)],
      }),
      geofenceType: new FormControl(options.geofenceType || 'circle', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      circleRadius: new FormControl(options.circleRadius || 1, {
        nonNullable: true,
        validators: [Validators.min(1), Validators.max(999)],
      }),
      unit: new FormControl(options.circleRadiusUnit || 'm', {
        nonNullable: true,
      }),
      polygonList: new FormControl(options.polygonList || [], {
        nonNullable: true,
      }),
      numberOfVertices: new FormControl(options.numberOfVertices || 3, {
        nonNullable: true,
        validators: [Validators.min(3), Validators.max(5)],
      }),
    });
    this.switchGeofenceFormValidation();

    // 入力内容が更新されたらバリデーションとマップ表示を更新する
    this.formGroup.valueChanges.subscribe(() => {
      this.switchGeofenceFormValidation();
      this.updateShipmentLocation();
    });
  }

  public ngOnInit(): void {
    // 更新時は初期表示から位置情報を表示する
    // マップコンポーネントの仕様上、変更検知で位置情報が表示されるため一定時間待ってから実行
    // その後、ルートに使われていれば入力を無効化する
    setTimeout(() => {
      if (this.updating) {
        this.updateShipmentLocation();

        if (this.isUse) {
          this.formGroup.controls.locationName.disable();
          this.formGroup.controls.address.disable();
        }
      }
    });
  }

  /**
   * 送信
   */
  public async submit() {
    const locationName = this.formGroup.get('locationName')?.value || '';
    const operation = this.updating ? '変更' : '新規登録';
    const options: AlertDialogOption = {
      type: 'warn',
      title: 'Confirm',
      message: `位置情報「${locationName}」を${operation}してよろしいですか？`,
      operation: operation,
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    if (this.isShipping) {
      const options: AlertDialogOption = {
        type: 'warn',
        title: 'Confirm',
        message: `位置情報「${locationName}」は、配送が開始された配送設定に設定されています。\n本当に更新してよろしいですか？`,
        operation: operation,
      };

      const reResult: boolean = await firstValueFrom(
        this.dialog
          .open(AlertDialogComponent, {
            width: '700px',
            data: options,
          })
          .afterClosed(),
      );

      if (!reResult) {
        return;
      }
    }

    const circleRadius: number = this.formGroup.get('circleRadius')?.value || 0;
    const circleRadiusUnit: UnitType = this.formGroup.get('unit')?.value || 'm';

    const circleRadiusInMeters = this.calcCircleRadius(
      circleRadiusUnit,
      circleRadius,
    );

    const newShipmentLocation: ShipmentLocationEditViewModel = {
      updating: this.updating,
      address: this.formGroup.get('address')?.value || '',
      locationName: this.formGroup.get('locationName')?.value || '',
      geofenceType: this.formGroup.get('geofenceType')?.value || 'circle',
      circleRadius: circleRadius,
      circleRadiusUnit: circleRadiusUnit,
      circleRadiusInMeters: circleRadiusInMeters,
      numberOfVertices: this.formGroup.get('numberOfVertices')?.value || 0,
      polygonList: this.formGroup.get('polygonList')?.value || [],
      latitude: this.formGroup.get('latitude')?.value || 0,
      longitude: this.formGroup.get('longitude')?.value || 0,
      postalCode: this.formGroup.get('postalCode')?.value || '',
      isUse: this.isUse,
      isShipping: this.isShipping,
    };
    this.dialogRef.close(newShipmentLocation);
  }

  /** 登録済み位置情報の表示有無を取得 */
  public toggleRegisteredLocationListDisplay(
    matSlideToggleChange: MatSlideToggleChange,
  ): void {
    this.isRegisteredLocationVisible = matSlideToggleChange.checked;
    if (matSlideToggleChange.checked) {
      this.displayRegisteredLocationList = this.registeredLocationList;
    } else {
      this.displayRegisteredLocationList = [];
    }
  }

  /**
   * 場所候補を検索する
   */
  public async searchPlaceSuggestionsList() {
    const addressValue = this.formGroup.get('address')?.value;
    if (!addressValue) return;

    // 初期化する
    this.placeSuggestionList = [];
    this.formGroup.get('postalCode')?.setValue(null, { emitEvent: false });
    this.formGroup.get('latitude')?.setValue(null, { emitEvent: false });
    this.formGroup.get('longitude')?.setValue(null, { emitEvent: false });
    this.formGroup.get('polygonList')?.setValue([], { emitEvent: false });
    this.updateShipmentLocation();

    let placeSuggestionList = await firstValueFrom(
      this.shipmentLocationService.findPlaceSuggestionsList(addressValue),
    );
    placeSuggestionList = placeSuggestionList.map((placeSuggestion) => {
      const address = this.formateAddress(placeSuggestion.text);
      return {
        text: address,
        placeId: placeSuggestion.placeId,
      };
    });

    this.placeSuggestionList = placeSuggestionList;
    this.autocompleteTrigger.openPanel();

    // 候補が何も見つからなかった場合
    if (!this.placeSuggestionList.length) {
      const alertDialogOptions: AlertDialogOption = {
        type: 'info',
        message: `住所の候補が見つかりませんでした。\n別のキーワードでお試しください。`,
      };
      this.dialog.open(AlertDialogComponent, {
        width: '450px',
        data: alertDialogOptions,
      });
    }
  }

  /**
   * 場所候補を選択した際の処理
   *
   * @param event 選択イベント
   */
  public async onSelectedPlace(event: MatAutocompleteSelectedEvent) {
    const addressValue: string = event.option.value;
    const place = this.placeSuggestionList.find(
      (placeSuggestion) => placeSuggestion.text === addressValue,
    );
    if (place) {
      // マップ上でクリックした場所から検索したい場合(placeIdが不明の場合)
      if (!place.placeId) {
        const placeDetail = this.placeDetailList.find(
          (placeDetail) => placeDetail.address === addressValue,
        );
        // フォームを更新する
        const postalCode = placeDetail?.postalCode?.replace('-', '');
        this.formGroup.get('postalCode')?.setValue(postalCode ?? null, {
          emitEvent: false,
        });
        this.formGroup
          .get('latitude')
          ?.setValue(placeDetail?.latitude ?? null, { emitEvent: false });
        this.formGroup
          .get('longitude')
          ?.setValue(placeDetail?.longitude ?? null, { emitEvent: false });
        this.formGroup.get('polygonList')?.setValue([], { emitEvent: false });

        // マップに表示する
        this.updateShipmentLocation();
        return;
      }

      // 住所からの検索をしたい場合
      await this.searchPlace(place.placeId);
    }
  }

  /**
   * ポリゴンリストを設定する
   *
   * @param polygonList ポリゴンリスト
   */
  public setPolygonList(polygonList: ShipmentGeofencePolygonDto[]) {
    this.formGroup.controls.polygonList.setValue(polygonList, {
      emitEvent: false,
    });
    const geofence = this.shipmentLocation?.geofence;
    if (geofence?.geofenceType === 'polygon') {
      geofence.polygonList = polygonList;
    }
  }

  /** マップでクリックした箇所にマーカーを表示させる */
  public async setShipmentLocation(
    lngLat: ShipmentGeofencePolygonViewModel,
  ): Promise<void> {
    this.shipmentLocation = {
      locationId: '',
      locationName: '',
      latitude: lngLat.latitude,
      longitude: lngLat.longitude,
      address: '',
      postalCode: '',
      geofence: undefined,
    };

    // 住所フォームのクリア
    // 変更検知させると画面上クリックした場所にマーカー表示されないため、変更イベントを発火させない
    this.formGroup.get('address')?.setValue('', { emitEvent: false });

    // 位置情報のオートコンプリート候補を取得
    this.shipmentLocationService
      .findPlaceListByPosition(lngLat.latitude, lngLat.longitude)
      .subscribe({
        next: (shipmentPlaceList) => {
          this.placeDetailList = shipmentPlaceList.map((shipmentPlace) => {
            const placeDetail = shipmentPlace;
            placeDetail.latitude = lngLat.latitude;
            placeDetail.longitude = lngLat.longitude;
            return placeDetail;
          });

          this.placeSuggestionList = shipmentPlaceList.map((shipmentPlace) => {
            return {
              text: shipmentPlace.address,
              placeId: '',
            };
          });

          // 候補が何も見つからなかった場合
          if (!this.placeSuggestionList.length) {
            const alertDialogOptions: AlertDialogOption = {
              type: 'info',
              message: `住所の候補が見つかりませんでした。\n別の場所を指定してください。`,
            };
            this.dialog.open(AlertDialogComponent, {
              width: '450px',
              data: alertDialogOptions,
            });
            return;
          }
          // 緯度経度を除き、オートコンプリート候補先頭の位置情報をフォームに追加する
          const nearestPlaceDetail = this.placeDetailList[0];

          // フォームを更新する
          this.formGroup
            .get('address')
            ?.setValue(nearestPlaceDetail.address, { emitEvent: false });
          const postalCode = nearestPlaceDetail?.postalCode?.replace('-', '');
          this.formGroup
            .get('postalCode')
            ?.setValue(postalCode ?? null, { emitEvent: false });
          this.formGroup
            .get('latitude')
            ?.setValue(lngLat.latitude, { emitEvent: false });
          this.formGroup
            .get('longitude')
            ?.setValue(lngLat.longitude, { emitEvent: false });
          this.formGroup.get('polygonList')?.setValue([], { emitEvent: false });

          // マップに表示する
          this.updateShipmentLocation();
        },
      });
  }

  /**
   * 位置情報住所フォームをクリアする。
   */
  public clearAddress(): void {
    // 値をクリア
    this.formGroup.controls.address.setValue('', { emitEvent: false });
    // マップ表示を解除
    this.formGroup.controls.longitude.setValue(null, { emitEvent: false });
    this.formGroup.controls.latitude.setValue(null, { emitEvent: false });
    this.formGroup.controls.polygonList.setValue([], { emitEvent: false });

    this.updateShipmentLocation();
  }

  /**
   * ジオフェンスの単位に応じて半径を計算する
   *
   * @param unit 単位
   * @param circleRadius 単位抜きの半径
   */
  private calcCircleRadius(unit: UnitType, circleRadius: number): number {
    if (unit === 'km') return circleRadius * 1000;
    else if (unit === 'mile') return circleRadius * 1609.344;
    else return circleRadius; // unit === 'm'のとき
  }

  private switchGeofenceFormValidation() {
    // ジオフェンスのタイプに応じてバリデーションを切り替える
    const geofenceType = this.formGroup.get('geofenceType')?.value || 'circle';

    const circleRadiusControl = this.formGroup.get('circleRadius');
    const unitControl = this.formGroup.get('unit');
    const numberOfVerticesControl = this.formGroup.get('numberOfVertices');
    const polygonListControl = this.formGroup.get('polygonList');

    if (geofenceType === 'polygon') {
      circleRadiusControl?.clearValidators();
      unitControl?.clearValidators();
      this.formGroup.removeValidators(this.radiusValidator);

      numberOfVerticesControl?.addValidators([
        Validators.required,
        Validators.min(3),
        Validators.max(5),
      ]);
      polygonListControl?.addValidators(Validators.required);
    } else {
      numberOfVerticesControl?.clearValidators();
      polygonListControl?.clearValidators();

      circleRadiusControl?.addValidators([
        Validators.required,
        Validators.min(1),
        Validators.max(999),
      ]);

      unitControl?.addValidators(Validators.required);
      this.formGroup.addValidators(this.radiusValidator);

      // フォームの状態を更新
      this.formGroup.updateValueAndValidity({ emitEvent: false });
    }
  }

  /** 単位別に半径のバリデーション */
  private radiusValidator(group: AbstractControl): ValidationErrors | null {
    const circleRadiusControl = group.get('circleRadius');
    const circleRadius: number = circleRadiusControl?.value;
    const unit: UnitType = group.get('unit')?.value;
    const unitRadioList = {
      m: 1,
      km: 1000,
      mile: 1609.34,
    };
    const ratio: number = unitRadioList[unit];

    const radius: number = ratio * circleRadius;
    const result = radius > 100000 ? { radiusRange: true } : null;

    if (!circleRadiusControl) {
      return null;
    }

    if (
      circleRadiusControl.errors &&
      !circleRadiusControl.errors['radiusRange']
    ) {
      return null;
    }

    circleRadiusControl?.setErrors(result);
    return result;
  }

  private async searchPlace(placeId: string): Promise<void> {
    const place = await firstValueFrom(
      this.shipmentLocationService.findPlace(placeId),
    );

    // フォームを更新する
    const postalCode = place.postalCode?.replace('-', '');
    this.formGroup
      .get('postalCode')
      ?.setValue(postalCode ?? null, { emitEvent: false });
    this.formGroup
      .get('latitude')
      ?.setValue(place.latitude, { emitEvent: false });
    this.formGroup
      .get('longitude')
      ?.setValue(place.longitude, { emitEvent: false });
    this.formGroup.get('polygonList')?.setValue([], { emitEvent: false });

    // マップに表示する
    this.updateShipmentLocation();
  }

  private updateShipmentLocation() {
    const latitude = this.formGroup.get('latitude')?.value;
    const longitude = this.formGroup.get('longitude')?.value;

    if (latitude == null || longitude == null) {
      this.shipmentLocation = undefined;
      return;
    }

    let geofence: ShipmentGeofenceViewModel | undefined;
    const geofenceType = this.formGroup.get('geofenceType')?.value;
    if (geofenceType === 'polygon') {
      const numberOfVertices =
        this.formGroup.get('numberOfVertices')?.value ?? 3;
      let polygonList = this.formGroup.get('polygonList')?.value || [];

      // 初期表示や頂点の数の変更時に、自分を中心に円周上に頂点を配置する
      // polygonListの最初と最後は同じ頂点なので、頂点数としては1つ減らして比較する
      if (
        numberOfVertices >= 3 &&
        (polygonList.length < 1 || numberOfVertices !== polygonList.length - 1)
      ) {
        const options: { steps: number; units: turf.Units } = {
          steps: numberOfVertices,
          units: 'meters',
        };
        const center = [longitude, latitude];
        const circle = turf.circle(center, DEFAULT_RADIUS_METERS, options);
        polygonList = circle.geometry.coordinates[0].map((pos) => {
          const polygon: ShipmentGeofencePolygonDto = {
            longitude: pos[0],
            latitude: pos[1],
          };
          return polygon;
        });
        this.formGroup
          .get('polygonList')
          ?.setValue(polygonList, { emitEvent: false });
      }

      geofence = {
        geofenceType: 'polygon',
        polygonList: polygonList,
      };
    } else {
      const circleRadius: number =
        this.formGroup.get('circleRadius')?.value ?? DEFAULT_RADIUS_METERS;
      const circleRadiusUnit: UnitType =
        this.formGroup.get('unit')?.value || 'm';
      geofence = {
        geofenceType: 'circle',
        circleRadius: circleRadius,
        circleRadiusUnit: circleRadiusUnit,
        circleRadiusInMeters: this.calcCircleRadius(
          circleRadiusUnit,
          circleRadius,
        ),
      };
    }

    let address = this.formGroup.get('address')?.value || '';
    address = this.formateAddress(address);

    const locationName = this.formGroup.get('locationName')?.value || '';
    const postalCode = this.formGroup.get('postalCode')?.value || '';
    this.shipmentLocation = {
      locationId: '',
      locationName: locationName,
      latitude: latitude,
      longitude: longitude,
      address: address,
      postalCode: postalCode,
      geofence: geofence,
    };
  }

  private formateAddress(address: string) {
    // Amazon Location Serviceの住所テキストには
    // 先頭に郵便番号が入ることがあるため、削除する
    return address.replace(/^〒\d{3}-\d{4}\s/, '');
  }
}
